/**
 * Project Switch Lock
 *
 * Prevents race conditions from rapid project switching by ensuring only
 * one project switch flow runs at a time. Each new switch aborts the previous.
 *
 * Semantics: "last writer wins" — the most recent project click is the
 * one that completes; all prior in-flight flows are cancelled.
 *
 * Usage:
 *   const signal = acquireProjectSwitchLock();
 *   await someAsyncWork();
 *   if (signal.aborted) return; // superseded by a newer switch
 */

let currentController: AbortController | null = null;

/**
 * Acquire the project switch lock. Aborts any previous in-flight switch.
 * Returns the AbortSignal for the new operation — callers should check
 * signal.aborted after every async boundary.
 */
export function acquireProjectSwitchLock(): AbortSignal {
  if (currentController) {
    console.log('🔒 [LOCK] Aborting previous project switch');
    currentController.abort();
  }
  currentController = new AbortController();
  return currentController.signal;
}

/**
 * Check if the given signal has been superseded (aborted).
 * Convenience wrapper for readability at checkpoints.
 */
export function isProjectSwitchSuperseded(signal: AbortSignal): boolean {
  return signal.aborted;
}
